'use client';

import { formatLocaleOptionLabel, type SupportedLocale } from '@/i18n/config';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { useMemo } from 'react';

type SignupLanguageCardCopy = {
  websiteLanguage: string;
  websiteLanguageOptional: string;
  chooseLanguageLater: string;
  selectLanguage: string;
  searchLanguages: string;
  noLanguagesFound: string;
};

type SignupLanguageCardProps = {
  copy: SignupLanguageCardCopy;
  selectedLanguage: SupportedLocale;
  sortedLocales: readonly SupportedLocale[];
  disabled?: boolean;
  onLanguageSelect: (locale: string) => void;
  onDefer: () => void;
};

export function SignupLanguageCard({
  copy,
  selectedLanguage,
  sortedLocales,
  disabled = false,
  onLanguageSelect,
  onDefer,
}: SignupLanguageCardProps) {
  const languageOptions = useMemo(
    () =>
      sortedLocales.map((locale) => ({
        value: locale,
        label: formatLocaleOptionLabel(locale),
      })),
    [sortedLocales]
  );

  return (
    <div className="bg-background/80 border-border/35 space-y-3 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1 text-start">
          <label htmlFor="signup-language" className="text-sm font-medium">
            {copy.websiteLanguage}
          </label>
          <p className="text-muted-foreground text-xs">
            {copy.websiteLanguageOptional}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground h-auto px-2 py-1 text-xs"
          onClick={onDefer}
          disabled={disabled}
        >
          {copy.chooseLanguageLater}
        </Button>
      </div>

      <Combobox
        id="signup-language"
        options={languageOptions}
        value={selectedLanguage}
        onValueChange={(locale) => {
          if (locale) onLanguageSelect(locale);
        }}
        placeholder={copy.selectLanguage}
        searchPlaceholder={copy.searchLanguages}
        emptyText={copy.noLanguagesFound}
        className="h-10"
        disabled={disabled}
      />
    </div>
  );
}
